/**
 * What every workspace page shares: the async-read hook and the failure
 * panel that turns an `ApiFailure` into the right sentence.
 *
 * The failure panel is the global state vocabulary from the handoff —
 * loading, offline, conflict, forbidden, failed — rendered one way, so a
 * network blip on the board reads exactly like a network blip on the team
 * page. A session that has expired is not rendered at all: the guard
 * redirects to sign-in with the current location preserved as `next`.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiFailure } from '../api.js';
import { useWorkspaceCopy } from '../components.js';
import { Button, InlineAlert, Spinner } from '../ui.js';

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly failure: ApiFailure }
  | { readonly status: 'ready'; readonly value: T };

/**
 * Load something, with reload. StrictMode-safe and unmount-safe: a stale
 * resolution is dropped rather than applied over a newer one.
 */
export function useAsync<T>(load: () => Promise<T>): {
  readonly state: AsyncState<T>;
  readonly reload: () => void;
} {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const generation = useRef(0);

  const run = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;
    setState({ status: 'loading' });
    load().then(
      (value) => {
        if (generation.current === mine) setState({ status: 'ready', value });
      },
      (failure: unknown) => {
        if (generation.current === mine) {
          setState({
            status: 'failed',
            failure: failure instanceof ApiFailure ? failure : new ApiFailure('failed'),
          });
        }
      },
    );
  }, [load]);

  useEffect(() => {
    run();
    return () => {
      generation.current += 1;
    };
  }, [run]);

  return { state, reload: run };
}

/** The one way a page-level failure is shown. */
export function FailurePanel({
  failure,
  onRetry,
}: {
  readonly failure: ApiFailure;
  readonly onRetry: () => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const navigate = useNavigate();
  const location = useLocation();

  // An expired session is a redirect, not a panel: the person signs in and
  // comes straight back to where they were.
  useEffect(() => {
    if (failure.kind === 'unauthenticated') {
      const next = encodeURIComponent(location.pathname + location.search);
      void navigate(`/signin?next=${next}`, { replace: true });
    }
  }, [failure.kind, navigate, location]);

  if (failure.kind === 'unauthenticated') return null;

  const [title, body] =
    failure.kind === 'offline'
      ? [copy.states.offlineTitle, copy.states.offlineBody]
      : failure.kind === 'conflict'
        ? [copy.states.conflictTitle, copy.states.conflictBody]
        : failure.kind === 'rate_limited'
          ? [copy.states.failedTitle, copy.states.rateLimited]
          : [copy.states.failedTitle, copy.states.failedBody];

  return (
    <InlineAlert
      tone={failure.kind === 'offline' ? 'warning' : 'error'}
      title={title}
      role="alert"
    >
      <p style={{ marginBottom: 10 }}>{body}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {copy.actions.retry}
      </Button>
    </InlineAlert>
  );
}

export function PageTitle({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <h1
      style={{
        font: 'var(--type-display-md)',
        letterSpacing: 'var(--type-display-md-tracking)',
        color: 'var(--text-primary)',
        marginBottom: 'var(--space-lg)',
      }}
    >
      {children}
    </h1>
  );
}

export function SectionHeading({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  return (
    <h2
      style={{
        font: 'var(--type-heading-sm)',
        letterSpacing: 'var(--type-heading-sm-tracking)',
        color: 'var(--text-primary)',
        margin: '0 0 var(--space-sm)',
      }}
    >
      {children}
    </h2>
  );
}

export function LoadingPanel(): ReactNode {
  const copy = useWorkspaceCopy();
  return <Spinner label={copy.states.loading} />;
}
